// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import PhotoSessionGroupContact from "./photo-session-group-contact.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "token": {
      "column": {
        "default": null,
        "name": "token",
        "null": false,
        "type": "string"
      },
      "name": "token",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "PhotoSessionGroupContactToken",
  "collectionKey": "photo_session_group_contact_tokens",
  "collectionName": "photo_session_group_contact_tokens",
  "i18nKey": "photo_session_group_contact_token",
  "camelizedLower": "photoSessionGroupContactToken",
  "name": "PhotoSessionGroupContactToken",
  "nameDasherized": "photo-session-group-contact-token",
  "pluralName": "photo_session_group_contact_tokens",
  "ransackable_associations": [
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "PhotoSessionGroupContact",
      "collectionName": "photo_session_group_contacts",
      "foreignKey": "photo_session_group_contact_id",
      "name": "photo_session_group_contact",
      "macro": "belongs_to",
      "resource_name": "PhotoSessionGroupContact",
      "through": null
    },
    {
      "className": "PhotoSessionGroupContactTokenContact",
      "collectionName": "photo_session_group_contact_token_contacts",
      "foreignKey": "photo_session_group_contact_token_id",
      "name": "photo_session_group_contact_token_contacts",
      "macro": "has_many",
      "resource_name": "PhotoSessionGroupContactTokenContact",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "photo_session_group_contact_id",
      "column": {
        "default": null,
        "name": "photo_session_group_contact_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "token",
      "column": {
        "default": null,
        "name": "token",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "PhotoSessionGroupContact",
      "collectionName": "photo_session_group_contacts",
      "foreignKey": "photo_session_group_contact_id",
      "name": "photo_session_group_contact",
      "macro": "belongs_to",
      "resource_name": "PhotoSessionGroupContact",
      "through": null
    }
  ],
  "paramKey": "photo_session_group_contact_token",
  "primaryKey": "id"
}

/** Frontend model for PhotoSessionGroupContactToken. */
export default class PhotoSessionGroupContactToken extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  token() {
    return this.readAttributeUnderscore("token")
  }

  /** @returns {boolean} */
  hasToken() {
    const value = this.token()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static createFromToken(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (BaseModel._callCollectionCommand(
      {
        args,
        command: "create_from_token",
        collectionName: "photo_session_group_contact_tokens",
        type: "collection"
      },
      commandArgs
    ))
  }

  /** @returns {import("./photo-session-group-contact.js").default | null} */
  photoSessionGroupContact() {
    return this._readBelongsToReflection({modelClass: PhotoSessionGroupContact, reflectionName: "photo_session_group_contact"})
  }

  /** @returns {Promise<import("./photo-session-group-contact.js").default | null>} */
  loadPhotoSessionGroupContact() {
    if (!("photoSessionGroupContactId" in this)) throw new Error("Foreign key method wasn't defined: photoSessionGroupContactId")

    const id = this.photoSessionGroupContactId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "photo_session_group_contact",
        model: this,
        modelClass: PhotoSessionGroupContact
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./photo-session-group-contact.js").default | null>} */
  ensurePhotoSessionGroupContactLoaded() {
    return this.ensureAssociationLoaded("photo_session_group_contact")
  }
}
